import {
  AI_ASSISTANT_RICH_CONTENT_TAGS,
  AIAssistantRichContentTagType,
} from "@/config/aiAssistantRichContent";

export type CopyFormat = "plain" | "rich" | "markdown";

export interface CopyFormatOptions {
  format: CopyFormat;
  content: any;
  title?: string;
}

/**
 * Tag conversion mappings for different formats
 */
const TAG_CONVERSIONS = {
  INFO_BOX: {
    plain: (content: string) => `ℹ️ INFO: ${content}`,
    markdown: (content: string) => `> ℹ️ **Info:** ${content}`,
    rich: (content: string) =>
      `<div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 16px; border-radius: 0 8px 8px 0; margin: 8px 0;"><strong>ℹ️ Info:</strong> ${content}</div>`,
  },

  HIGHLIGHT: {
    plain: (content: string) => `✨ ${content}`,
    markdown: (content: string) => `**${content}**`,
    rich: (content: string) =>
      `<mark style="background-color: #fef3c7; padding: 4px 8px; border-radius: 4px; font-weight: 500;">${content}</mark>`,
  },

  ACTION_ITEM: {
    plain: (content: string) => `🎯 ACTION: ${content}`,
    markdown: (content: string) => `> 🎯 **Action Required:** ${content}`,
    rich: (content: string) =>
      `<div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; padding: 12px; border-radius: 8px; margin: 8px 0; font-weight: 500; color: #166534;"><strong>🎯 Action Required:</strong> ${content}</div>`,
  },

  WARNING: {
    plain: (content: string) => `⚠️ WARNING: ${content}`,
    markdown: (content: string) => `> ⚠️ **Warning:** ${content}`,
    rich: (content: string) =>
      `<div style="background-color: #fef2f2; border-left: 4px solid #ef4444; padding: 16px; border-radius: 0 8px 8px 0; margin: 8px 0; color: #b91c1c;"><strong>⚠️ Warning:</strong> ${content}</div>`,
  },

  CODE_BLOCK: {
    plain: (content: string) => `CODE:\n${content}`,
    markdown: (content: string) => `\`\`\`\n${content}\n\`\`\``,
    rich: (content: string) =>
      `<pre style="background-color: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; padding: 16px; font-family: 'Courier New', monospace; font-size: 14px; overflow-x: auto; margin: 8px 0;"><code>${content}</code></pre>`,
  },

  QUOTE: {
    plain: (content: string) => `"${content}"`,
    markdown: (content: string) => `> ${content}`,
    rich: (content: string) =>
      `<blockquote style="border-left: 4px solid #9ca3af; padding-left: 16px; font-style: italic; color: #6b7280; margin: 8px 0;">${content}</blockquote>`,
  },

  CALLOUT: {
    plain: (content: string) => `💡 NOTE: ${content}`,
    markdown: (content: string) => `> 💡 **Note:** ${content}`,
    rich: (content: string) =>
      `<div style="background-color: #faf5ff; border: 1px solid #d8b4fe; padding: 16px; border-radius: 8px; margin: 8px 0; color: #6b21a8;"><strong>💡 Note:</strong> ${content}</div>`,
  },

  TASK: {
    plain: (content: string) => `☐ TASK: ${content}`,
    markdown: (content: string) => `- [ ] ${content}`,
    rich: (content: string) =>
      `<div style="display: flex; align-items: flex-start; gap: 12px; padding: 12px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; margin: 8px 0;"><input type="checkbox" style="margin-top: 2px;" disabled> <span>${content}</span></div>`,
  },

  CHECKLIST: {
    plain: (content: string) => `☐ ${content}`,
    markdown: (content: string) => `- [ ] ${content}`,
    rich: (content: string) =>
      `<div style="display: flex; align-items: center; gap: 8px; padding: 4px 0;"><input type="checkbox" disabled> <span>${content}</span></div>`,
  },

  COLOR_PREVIEW: {
    plain: (content: string) => `${content}`,
    markdown: (content: string) => `\`${content}\``,
    rich: (content: string) =>
      `<span style="display: inline-flex; align-items: center; gap: 8px;"><span style="width: 16px; height: 16px; background-color: ${content}; border: 1px solid #ccc; border-radius: 4px; display: inline-block;"></span><code>${content}</code></span>`,
  },

  TABLE: {
    plain: (content: string) => content.replace(/\|/g, " | "),
    markdown: (content: string) => content,
    rich: (content: string) => {
      // Convert markdown table to HTML table
      const lines = content.split("\n").filter((line) => line.trim());
      if (lines.length < 2) return content;

      const headers = lines[0]
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h);
      const rows = lines.slice(2).map((line) =>
        line
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell) => cell),
      );

      return `<table style="border-collapse: collapse; width: 100%; margin: 16px 0;">
        <thead>
          <tr style="background-color: #f9fafb;">
            ${headers.map((h) => `<th style="border: 1px solid #d1d5db; padding: 12px; text-align: left; font-weight: 600;">${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) =>
                `<tr>${row.map((cell) => `<td style="border: 1px solid #d1d5db; padding: 12px;">${cell}</td>`).join("")}</tr>`,
            )
            .join("")}
        </tbody>
      </table>`;
    },
  },
};

/**
 * Convert custom tags to specified format
 */
function convertCustomTags(content: string, format: CopyFormat): string {
  let convertedContent = content;

  // Process each tag type
  Object.entries(AI_ASSISTANT_RICH_CONTENT_TAGS).forEach(
    ([tagType, config]) => {
      const tagKey = tagType as AIAssistantRichContentTagType;
      const converter = TAG_CONVERSIONS[tagKey];

      if (converter && converter[format]) {
        convertedContent = convertedContent.replace(
          config.regex,
          (match, captured) => {
            return converter[format](captured || match);
          },
        );
      }
    },
  );

  return convertedContent;
}

/**
 * Convert content to plain text format
 */
function convertToPlainText(content: any, title?: string): string {
  let result = "";

  if (title) {
    result += `${title.toUpperCase()}\n${"=".repeat(title.length)}\n\n`;
  }

  if (typeof content === "string") {
    // Convert custom tags to plain text
    result += convertCustomTags(content, "plain");

    // Strip remaining HTML tags
    result = result.replace(/<[^>]*>/g, "");

    // Convert basic formatting
    result = result
      .replace(/\*\*(.*?)\*\*/g, "$1") // Bold
      .replace(/\*(.*?)\*/g, "$1") // Italic
      .replace(/`(.*?)`/g, "$1") // Code
      .replace(/#{1,6}\s+(.*)/g, "$1") // Headers
      .replace(/^\s*[-*+]\s+/gm, "• ") // Lists
      .replace(/^\s*\d+\.\s+/gm, "• "); // Numbered lists
  } else if (typeof content === "object" && content !== null) {
    // Handle structured object content
    Object.entries(content).forEach(([key, value]) => {
      const sectionTitle = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      result += `${sectionTitle}:\n${"-".repeat(sectionTitle.length)}\n`;

      if (typeof value === "string") {
        result += convertToPlainText(value) + "\n\n";
      } else {
        result += JSON.stringify(value, null, 2) + "\n\n";
      }
    });
  }

  return result.trim();
}

/**
 * Convert content to markdown format
 */
function convertToMarkdown(content: any, title?: string): string {
  let result = "";

  if (title) {
    result += `# ${title}\n\n`;
  }

  if (typeof content === "string") {
    // Convert custom tags to markdown
    result += convertCustomTags(content, "markdown");

    // Strip remaining HTML but preserve markdown
    result = result.replace(
      /<(?!\/?(em|strong|code|pre|blockquote)\b)[^>]*>/g,
      "",
    );

    // Convert HTML formatting to markdown
    result = result
      .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
      .replace(/<em>(.*?)<\/em>/g, "*$1*")
      .replace(/<code>(.*?)<\/code>/g, "`$1`")
      .replace(/<pre>(.*?)<\/pre>/gs, "```\n$1\n```")
      .replace(/<blockquote>(.*?)<\/blockquote>/gs, "> $1");
  } else if (typeof content === "object" && content !== null) {
    // Handle structured object content
    Object.entries(content).forEach(([key, value]) => {
      const sectionTitle = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      result += `## ${sectionTitle}\n\n`;

      if (typeof value === "string") {
        result += convertToMarkdown(value) + "\n\n";
      } else {
        result += "```json\n" + JSON.stringify(value, null, 2) + "\n```\n\n";
      }
    });
  }

  return result.trim();
}

/**
 * Convert content to rich text (HTML) format
 */
function convertToRichText(content: any, title?: string): string {
  let result = "";

  if (title) {
    result += `<h1 style="color: #1f2937; font-size: 28px; font-weight: bold; margin-bottom: 24px;">${title}</h1>`;
  }

  if (typeof content === "string") {
    // Convert custom tags to rich HTML
    result += convertCustomTags(content, "rich");

    // Ensure basic HTML formatting
    if (!result.includes("<")) {
      // Convert markdown-like formatting to HTML
      result = result
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(
          /`(.*?)`/g,
          '<code style="background-color: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace;">$1</code>',
        )
        .replace(
          /#{3}\s+(.*)/g,
          '<h3 style="color: #374151; font-size: 20px; font-weight: 600; margin: 16px 0 8px 0;">$1</h3>',
        )
        .replace(
          /#{2}\s+(.*)/g,
          '<h2 style="color: #374151; font-size: 24px; font-weight: 600; margin: 20px 0 12px 0;">$1</h2>',
        )
        .replace(
          /#{1}\s+(.*)/g,
          '<h1 style="color: #1f2937; font-size: 28px; font-weight: bold; margin: 24px 0 16px 0;">$1</h1>',
        )
        .replace(
          /\n\n/g,
          '</p><p style="margin: 12px 0; line-height: 1.6; color: #374151;">',
        )
        .replace(/^\s*[-*+]\s+(.+)/gm, '<li style="margin: 4px 0;">$1</li>');

      // Wrap in paragraph tags if not already wrapped
      if (
        !result.includes("<p>") &&
        !result.includes("<div>") &&
        !result.includes("<h")
      ) {
        result = `<p style="margin: 12px 0; line-height: 1.6; color: #374151;">${result}</p>`;
      }
    }
  } else if (typeof content === "object" && content !== null) {
    // Handle structured object content
    Object.entries(content).forEach(([key, value]) => {
      const sectionTitle = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      result += `<h2 style="color: #374151; font-size: 24px; font-weight: 600; margin: 24px 0 12px 0; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px;">${sectionTitle}</h2>`;

      if (typeof value === "string") {
        result += convertToRichText(value);
      } else {
        result += `<pre style="background-color: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; padding: 16px; font-family: monospace; font-size: 14px; overflow-x: auto; margin: 8px 0;"><code>${JSON.stringify(value, null, 2)}</code></pre>`;
      }
    });
  }

  return result;
}

/**
 * Main format conversion function
 */
export function formatContentForCopy({
  format,
  content,
  title,
}: CopyFormatOptions): string {
  switch (format) {
    case "plain":
      return convertToPlainText(content, title);
    case "markdown":
      return convertToMarkdown(content, title);
    case "rich":
      return convertToRichText(content, title);
    default:
      return String(content);
  }
}

/**
 * Copy content to clipboard with proper MIME types
 */
export async function copyToClipboard(
  formattedContent: string,
  format: CopyFormat,
): Promise<boolean> {
  try {
    if (format === "rich") {
      // For rich text, copy both HTML and plain text
      const plainFallback = convertToPlainText(formattedContent);

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([formattedContent], { type: "text/html" }),
          "text/plain": new Blob([plainFallback], { type: "text/plain" }),
        }),
      ]);
    } else {
      // For plain text and markdown, copy as text
      await navigator.clipboard.writeText(formattedContent);
    }

    return true;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);

    // Fallback for older browsers
    try {
      await navigator.clipboard.writeText(formattedContent);
      return true;
    } catch (fallbackError) {
      console.error("Fallback copy also failed:", fallbackError);
      return false;
    }
  }
}

/**
 * Get user-friendly format names
 */
export function getFormatDisplayName(format: CopyFormat): string {
  switch (format) {
    case "plain":
      return "Plain Text";
    case "rich":
      return "Rich Text";
    case "markdown":
      return "Markdown";
    default:
      return format;
  }
}

/**
 * Get format descriptions
 */
export function getFormatDescription(format: CopyFormat): string {
  switch (format) {
    case "plain":
      return "Universal compatibility - works everywhere";
    case "rich":
      return "Gmail, Google Docs, Word - preserves formatting";
    case "markdown":
      return "Slack, Discord, GitHub, Notion - developer friendly";
    default:
      return "";
  }
}
